import { CurrencyOrEmpty } from '@plato/app-shared/components/currency-or-empty';
import { MaskedContent } from '@plato/app-shared/components/masked-content';
import { PercentageOrEmpty } from '@plato/ui-legacy/components/typography/percentage-or-empty';
import {
    InfoHint,
    InfoHintBody,
    InfoHintContent,
    InfoHintHeader,
    InfoHintTitle,
    InfoHintTrigger,
} from '@plato/ui/components/info-hint';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { DataStatBlock } from '../../dashboard/components/data-stat-block.tsx';
import { useTeamFilters } from '../hooks/use-team-filters.ts';

/**
 * Team-total stat cards above the rep table: Revenue & GM, Plato Revenue,
 * Revenue at Risk, Open Opportunity.
 */
export function TeamStats() {
    const { t } = useTranslation('team');

    const { appliedFilters } = useTeamFilters();

    const { data, isLoading } = api.platoUser.query.notes.getStats.useQuery(appliedFilters);

    const periodRevenue = data?.periodRevenue ?? 0;
    const grossMarginPercent = periodRevenue === 0 ? 0 : (data?.periodGrossMargin ?? 0) / periodRevenue;

    const revenueLabelId = useId();
    const platoRevenueLabelId = useId();
    const churnLabelId = useId();
    const opportunityLabelId = useId();

    return (
        <DataStatBlock>
            {/* Results — team revenue + gross-margin rate */}
            <DataStatBlock.Item className="min-w-[180px] flex-1">
                <DataStatBlock.Title>
                    <div className="flex items-center justify-between">
                        <span id={revenueLabelId}>{t('stats_revenue')}</span>
                        <InfoHint>
                            <InfoHintTrigger />
                            <InfoHintContent side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('stats_revenue')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>{t('stats_revenue_tooltip')}</InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                </DataStatBlock.Title>
                <DataStatBlock.Body isLoading={isLoading}>
                    <span role="status" aria-labelledby={revenueLabelId}>
                        <MaskedContent>
                            <CurrencyOrEmpty decimals={0}>{periodRevenue}</CurrencyOrEmpty>
                        </MaskedContent>
                    </span>
                </DataStatBlock.Body>
                <DataStatBlock.Footer className="text-neutral-600" isLoading={isLoading}>
                    {/* Gross margin is hidden in Customer View, like everywhere in the app. */}
                    <MaskedContent>
                        <PercentageOrEmpty decimals={1}>{grossMarginPercent}</PercentageOrEmpty>{' '}
                        {t('stats_margin_label')}
                    </MaskedContent>
                </DataStatBlock.Footer>
            </DataStatBlock.Item>

            {/* Plato Revenue (team total, deduplicated across reps) */}
            <DataStatBlock.Item className="min-w-[180px] flex-1">
                <DataStatBlock.Title>
                    <div className="flex items-center justify-between">
                        <span id={platoRevenueLabelId}>{t('plato_revenue_total')}</span>
                        <InfoHint>
                            <InfoHintTrigger />
                            <InfoHintContent side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('plato_revenue_total')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>{t('tooltip_plato_revenue_total')}</InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                </DataStatBlock.Title>
                <DataStatBlock.Body isLoading={isLoading}>
                    <span role="status" aria-labelledby={platoRevenueLabelId}>
                        <CurrencyOrEmpty decimals={0}>{data?.revenueUpliftTotal ?? 0}</CurrencyOrEmpty>
                    </span>
                </DataStatBlock.Body>
                <DataStatBlock.Footer className="text-neutral-600" isLoading={isLoading}>
                    {t('plato_revenue_total_footer', {
                        value: data?.revenueUpliftInsightsCount ?? 0,
                    })}
                    {/* Share masked in Customer View: share + unmasked uplift would
                        reveal the masked total revenue. */}
                    {periodRevenue > 0 && (
                        <>
                            {' · '}
                            <MaskedContent>
                                <PercentageOrEmpty decimals={1}>
                                    {(data?.revenueUpliftTotal ?? 0) / periodRevenue}
                                </PercentageOrEmpty>{' '}
                                {t('plato_revenue_total_share_label')}
                            </MaskedContent>
                        </>
                    )}
                </DataStatBlock.Footer>
            </DataStatBlock.Item>

            {/* Risk — open churn € across the visible book */}
            <DataStatBlock.Item className="min-w-[180px] flex-1">
                <DataStatBlock.Title>
                    <div className="flex items-center justify-between">
                        <span id={churnLabelId}>{t('stats_churn')}</span>
                        <InfoHint>
                            <InfoHintTrigger />
                            <InfoHintContent side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('stats_churn')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>{t('stats_churn_tooltip')}</InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                </DataStatBlock.Title>
                <DataStatBlock.Body isLoading={isLoading}>
                    <span role="status" aria-labelledby={churnLabelId} className="text-red-600">
                        <CurrencyOrEmpty decimals={0}>{data?.churnOpenValue ?? 0}</CurrencyOrEmpty>
                    </span>
                </DataStatBlock.Body>
                <DataStatBlock.Footer className="text-neutral-600" isLoading={isLoading}>
                    {t('stats_churn_footer', { value: data?.churnOpenCount ?? 0 })}
                </DataStatBlock.Footer>
            </DataStatBlock.Item>

            {/* Opportunity — open upside not yet captured */}
            <DataStatBlock.Item className="min-w-[180px] flex-1">
                <DataStatBlock.Title>
                    <div className="flex items-center justify-between">
                        <span id={opportunityLabelId}>{t('stats_opportunity')}</span>
                        <InfoHint>
                            <InfoHintTrigger />
                            <InfoHintContent side="bottom">
                                <InfoHintHeader>
                                    <InfoHintTitle>{t('stats_opportunity')}</InfoHintTitle>
                                </InfoHintHeader>
                                <InfoHintBody>{t('stats_opportunity_tooltip')}</InfoHintBody>
                            </InfoHintContent>
                        </InfoHint>
                    </div>
                </DataStatBlock.Title>
                <DataStatBlock.Body isLoading={isLoading}>
                    <span role="status" aria-labelledby={opportunityLabelId} className="text-green-700">
                        <CurrencyOrEmpty decimals={0}>{data?.opportunityOpenValue ?? 0}</CurrencyOrEmpty>
                    </span>
                </DataStatBlock.Body>
                <DataStatBlock.Footer className="text-neutral-600" isLoading={isLoading}>
                    {t('stats_opportunity_footer', { value: data?.opportunityOpenCount ?? 0 })}
                </DataStatBlock.Footer>
            </DataStatBlock.Item>
        </DataStatBlock>
    );
}
